import React, { forwardRef, useContext } from "react";
import clsx from "clsx";
import { Form, Col, FormGroup, Row } from "react-bootstrap";
import PhoneInput from "react-phone-number-input";
import { touch, WrappedFieldProps, change } from "redux-form";

import { Skeleton, RequiredMark } from "components";
import { BrandingColorContext } from "components/contexts";
import { useLoader } from "hooks";

import styles from "./input.module.scss";

export type InputProps = WrappedFieldProps & {
	name: string;
	label?: string;
	blurLoading?: boolean;
	isRequired?: boolean;
	disabled?: boolean;
	formName?: string;
	maxLength?: number;
	colLabel?: number;
	colInput?: number;
};

const ControlInput = forwardRef<HTMLInputElement>((controlProps, ref) => {
	return <Form.Control ref={ref} {...controlProps} />;
});

export const PhoneInputRedux = ({
	input,
	meta: { error, touched, dispatch },
	label = "Phone",
	blurLoading,
	isRequired = false,
	disabled,
	formName,
	maxLength = 16,
	colLabel = 6,
	colInput = 6,
}: InputProps) => {
	const Loader = useLoader(blurLoading);
	const invalidClass = touched && error && styles.errorField;
	const disabledInputClass = disabled && styles.disabledPhoneInput;

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		if (!e.target.value) {
			formName && dispatch(change(formName, input.name, ""));
		}

		formName && dispatch(touch(formName, input.name));
	};

	const brandingColor = useContext(BrandingColorContext);

	return (
		<FormGroup
			className={clsx(disabled && styles.disablePhoneField)}
			as={Row}
			controlId={input.name}
		>
			<Form.Label column sm={colLabel}>
				{label}
				{isRequired && <RequiredMark />}
			</Form.Label>
			<Col sm={colInput}>
				<Loader
					skeleton={<Skeleton width="100%" height={34} />}
					component={
						<PhoneInput
							{...input}
							name={input.name}
							onBlur={handleBlur}
							defaultCountry="US"
							initialValueFormat="national"
							international={false}
							inputComponent={ControlInput}
							disabled={disabled}
							isInvalid={touched && !!error}
							className={clsx(
								brandingColor && "branding-color-container",
								invalidClass,
								disabledInputClass,
							)}
							maxLength={maxLength}
						/>
					}
				/>
				{error && touched && (
					<div
						className="errorMessage"
						style={{
							display: "block",
							marginTop: "3px",
						}}
					>
						{error}
					</div>
				)}
			</Col>
		</FormGroup>
	);
};
